import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { SSNInput } from "@/components/ui/ssn-input"
import { CheckCircle, ArrowRight, DollarSign, Calendar, Percent } from "lucide-react"
import { dollars } from '@prescreen/lib/formats'
import { CreditEvaluation, PrescreenRequest } from '@prescreen/lib/types'
import { apiFetch } from '@prescreen/lib/api-client'
import {
  BorrowerBrandingFrame,
  borrowerCompleteChrome,
  borrowerPageShellClass
} from '@/components/borrower/BorrowerBrandingFrame'

function CompletePageSsn(props: {
  value: string
  onChange: (value: string) => void
  error: string | null
}) {
  return (
    <div className="mx-auto mb-4 max-w-sm text-left">
      <Label htmlFor="completeSsn">Social Security Number *</Label>
      <SSNInput
        id="completeSsn"
        value={props.value}
        onChange={props.onChange}
        required
      />
      <p className="mt-1 text-xs text-gray-600">
        Required before a hard credit pull. Saving it does not change your prequalification result.
      </p>
      {props.error ? <p className="mt-1 text-sm text-red-600">{props.error}</p> : null}
    </div>
  )
}

function redirectToPartnerOrStay(
  returnUrl: string | undefined,
  requestId: string,
  selectedLoanId: string | null
): void {
  const trimmed = returnUrl?.trim()
  if (!trimmed) {
    return
  }
  try {
    const parsed = new URL(trimmed)
    if (selectedLoanId) {
      parsed.searchParams.set('selectedLoanId', selectedLoanId)
    }
    parsed.searchParams.set('requestId', requestId)
    window.location.href = parsed.toString()
  } catch {
    return
  }
}

export default function PrequalificationCompletePage() {
  const { requestId } = useParams<{ requestId: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<{
    returnUrl?: string;
    prescreenRequest: PrescreenRequest;
    evaluations: CreditEvaluation[];
  } | null>(null)
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectionSaved, setSelectionSaved] = useState(false)
  const [hasSsnOnFile, setHasSsnOnFile] = useState(true)
  const [ssn, setSsn] = useState('')
  const [ssnError, setSsnError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !requestId) {
        console.error('Missing token or requestId parameter')
        setIsLoading(false)
        return
      }

      try {
        const response = await apiFetch(`/api/prequalification/${requestId}?token=${token}`)
        const result = await response.json()

        if (result.success && result.data?.prescreenRequest) {
          const passingEvaluations = (result.data.evaluations || []).filter(
            (evaluation: CreditEvaluation) => evaluation.result === 'pass'
          )
          setData({
            returnUrl: typeof result.data.returnUrl === 'string' ? result.data.returnUrl : '',
            prescreenRequest: result.data.prescreenRequest,
            evaluations: passingEvaluations
          })

          setHasSsnOnFile(result.data.prescreenRequest.hasSsnOnFile !== false)
          if (passingEvaluations.length === 1) {
            setSelectedLoanId(passingEvaluations[0].loanProductId!)
          }
        }
      } catch (error) {
        console.error('Failed to fetch complete data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [requestId, token])

  const handleLoanSelect = (loanId: string) => {
    setSelectedLoanId(loanId)
  }

  const saveSsnIfNeeded = async (): Promise<boolean> => {
    if (hasSsnOnFile || !requestId || !token) {
      return true
    }
    const digits = ssn.replace(/\D/g, '')
    if (digits.length !== 9) {
      setSsnError('Enter a 9-digit Social Security Number.')
      return false
    }
    const response = await apiFetch('/api/prequalification/ssn', {
      method: 'POST',
      body: JSON.stringify({ requestId, token, ssn: digits })
    })
    const result = await response.json()
    if (!response.ok || !result.success) {
      setSsnError(result.error || 'Failed to save Social Security Number.')
      return false
    }
    setHasSsnOnFile(true)
    setSsn('')
    return true
  }

  const handleContinue = async () => {
    if (!data || !requestId || !token) return

    setSsnError(null)
    setIsSubmitting(true)
    try {
      const ssnSaved = await saveSsnIfNeeded()
      if (!ssnSaved) {
        return
      }

      if (data.evaluations.length > 0 && selectedLoanId) {
        const response = await apiFetch('/api/prequalification', {
          method: 'PATCH',
          body: JSON.stringify({
            requestId,
            selectedLoanId,
            token
          })
        })

        const result = await response.json()

        if (result.success) {
          redirectToPartnerOrStay(data.returnUrl, requestId, selectedLoanId)
          setSelectionSaved(true)
        } else {
          console.error('Failed to save loan selection:', result.error)
        }
      } else {
        redirectToPartnerOrStay(data.returnUrl, requestId, selectedLoanId)
      }
    } catch (err) {
      console.error('Failed to continue prequalification:', err)
      setSsnError('Failed to continue. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatLoanTerm = (months: number) => {
    const years = Math.floor(months / 12)
    const remainingMonths = months % 12
    
    if (years === 0) {
      return `${months} months`
    } else if (remainingMonths === 0) {
      return `${years} year${years > 1 ? 's' : ''}`
    } else {
      return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`
    }
  }

  const ssnDigits = ssn.replace(/\D/g, '')
  const ssnReady = hasSsnOnFile || ssnDigits.length === 9

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const branding = data?.prescreenRequest.branding
  const isBranded = Boolean(branding)
  const lenderName = isBranded ? branding.displayName : 'your lender'

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Error</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Failed to load prequalification data</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const hasLoanOptions = data.evaluations.length > 0

  if (hasLoanOptions) {
    const loanOptions = data.evaluations
      .map(evaluation => {
        const loanProduct = data.prescreenRequest.evaluateLoanOptions?.find(lp => lp.id === evaluation.loanProductId)
        if (!loanProduct) {
          return null
        }
        return {
          ...loanProduct,
          estimatedPayment: evaluation.estimatedPayment || 0
        }
      })
      .filter((option): option is NonNullable<typeof option> => option !== null)

    const loansByInstitution = loanOptions.reduce((acc, loan) => {
      const institutionId = loan.institutionId
      if (!acc[institutionId]) {
        acc[institutionId] = {
          institutionId,
          institutionName: loan.institutionName,
          institutionLogoUrl: loan.institutionLogoUrl,
          institutionLogoBackground: loan.institutionLogoBackground,
          loans: []
        }
      }
      acc[institutionId].loans.push(loan)
      return acc
    }, {} as Record<string, {
      institutionId: string
      institutionName: string
      institutionLogoUrl?: string
      institutionLogoBackground?: string
      loans: typeof loanOptions
    }>)

    const institutions = Object.values(loansByInstitution)

    return (
      <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(isBranded)}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${isBranded ? 'text-foreground' : 'text-gray-900'}`}>
              {data.evaluations.length === 1 ? 'Your Loan Option' : 'Choose Your Loan Option'}
            </h1>
            <p className={isBranded ? 'text-muted-foreground' : 'text-gray-600'}>
              {data.evaluations.length === 1 
                ? 'Great news! You qualify for this loan option. Review the details below and continue when ready.'
                : `Great news! You qualify for ${data.evaluations.length} loan options. Please select the option that works best for you.`
              }
            </p>
          </div>

          <div className="mb-6">
            <Card className={borrowerCompleteChrome.loanAmountCard(isBranded)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={borrowerCompleteChrome.loanAmountTitle(isBranded)}>Loan Amount</h3>
                    <p className={borrowerCompleteChrome.loanAmountValue(isBranded)}>
                      {dollars(data.prescreenRequest.loanAmount || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={borrowerCompleteChrome.loanAmountHint(isBranded)}>Requested Amount</p>
                    <p className={`text-xs ${isBranded ? 'text-muted-foreground' : 'text-blue-500'}`}>Based on your application</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {institutions.map((institution) => (
              <div key={institution.institutionId} className="bg-white text-gray-900 rounded-lg border shadow-sm overflow-hidden">
                <div className="bg-gray-50 border-b p-6">
                  <div className="flex items-center gap-4">
                    {institution.institutionLogoUrl && (
                      <div
                        className={
                          institution.institutionLogoBackground
                            ? 'flex shrink-0 items-center justify-center rounded-md p-2'
                            : undefined
                        }
                        style={
                          institution.institutionLogoBackground
                            ? { backgroundColor: institution.institutionLogoBackground }
                            : undefined
                        }
                      >
                        <img
                          src={institution.institutionLogoUrl}
                          alt={institution.institutionName}
                          className="h-12 w-auto max-w-[200px] object-contain"
                        />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-semibold">{institution.institutionName}</h2>
                      <p className="text-sm text-muted-foreground">
                        {institution.loans.length} {institution.loans.length === 1 ? 'loan option' : 'loan options'} available
                      </p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {institution.loans.map((loan) => (
                    <button
                      key={loan.id}
                      type="button"
                      className={`w-full p-6 text-left cursor-pointer transition-all duration-200 ${
                        borrowerCompleteChrome.selectedLoanRow(
                          isBranded,
                          selectedLoanId === loan.id
                        )
                      }`}
                      onClick={() => handleLoanSelect(loan.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-semibold">{loan.name}</h3>
                            {selectedLoanId === loan.id && (
                              <CheckCircle className={`w-5 h-5 flex-shrink-0 ${borrowerCompleteChrome.selectedLoanCheck(isBranded)}`} />
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                              <div className="flex items-center text-gray-600 mb-2">
                                <DollarSign className="w-4 h-4 mr-1.5 flex-shrink-0" />
                                <span className="text-sm">Monthly Payment</span>
                              </div>
                              <div className="font-semibold text-lg">
                                {dollars(loan.estimatedPayment)}
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex items-center text-gray-600 mb-2">
                                <Calendar className="w-4 h-4 mr-1.5 flex-shrink-0" />
                                <span className="text-sm">Term</span>
                              </div>
                              <div className="font-semibold text-lg">
                                {formatLoanTerm(loan.loanTermMonths)}
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex items-center text-gray-600 mb-2">
                                <Percent className="w-4 h-4 mr-1.5 flex-shrink-0" />
                                <span className="text-sm">Interest Rate</span>
                              </div>
                              <div className="font-semibold text-lg">
                                {(loan.interestRate * 1).toFixed(3)}%
                              </div>
                            </div>
                            
                            <div>
                              <div className="text-gray-600 mb-2">
                                <span className="text-sm">APR</span>
                              </div>
                              <div className="font-semibold text-lg">
                                {(loan.advertisedApr * 1).toFixed(3)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            {hasSsnOnFile ? null : (
              <CompletePageSsn value={ssn} onChange={setSsn} error={ssnError} />
            )}
            <Button 
              onClick={handleContinue}
              disabled={!selectedLoanId || isSubmitting || !ssnReady}
              className={borrowerCompleteChrome.continueButtonClass(isBranded)}
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5 mr-2" />
                  {data.evaluations.length === 1 ? 'Continue with This Loan' : 'Continue with Selected Loan'}
                </>
              )}
            </Button>
            
            {!selectedLoanId && data.evaluations.length > 1 && (
              <p className="text-sm text-gray-500 mt-2">
                Please select a loan option to continue
              </p>
            )}
            {selectionSaved && !data.returnUrl?.trim() && (
              <p className="text-sm text-green-700 mt-2">
                Loan selection saved.
              </p>
            )}
          </div>

          <div className={`mt-6 text-center text-sm ${isBranded ? 'text-muted-foreground' : 'text-gray-500'}`}>
            <p>Request ID: {requestId}</p>
          </div>
        </div>
      </BorrowerBrandingFrame>
    )
  }

  return (
    <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(isBranded)}>
      <div className="max-w-2xl mx-auto px-4 flex items-center justify-center min-h-[60vh]">
        <Card className="text-center w-full">
          <CardHeader>
            <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${borrowerCompleteChrome.successIconWrap(isBranded)}`}>
              <CheckCircle className={`w-8 h-8 ${borrowerCompleteChrome.successIcon(isBranded)}`} />
            </div>
            <CardTitle className="text-2xl text-gray-900">
              Prequalification Complete
            </CardTitle>
            <CardDescription className="text-lg">
              Your credit prequalification has been processed and the results have been sent to {lenderName}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className={`rounded-lg p-4 border ${borrowerCompleteChrome.infoPanel(isBranded)}`}>
              <h3 className={borrowerCompleteChrome.infoTitle(isBranded)}>What happens next?</h3>
              <ul className={borrowerCompleteChrome.infoList(isBranded)}>
                <li>• Your prequalification results have been securely transmitted to {lenderName}</li>
                <li>• {isBranded ? branding.displayName : 'Your lender'} will review the results and contact you directly</li>
                <li>• This soft credit check will not affect your credit score</li>
                <li>• You should hear from {lenderName} within 1-2 business days</li>
              </ul>
            </div>

            <div className="space-y-4">
              {hasSsnOnFile ? null : (
                <CompletePageSsn value={ssn} onChange={setSsn} error={ssnError} />
              )}
              <Button 
                onClick={handleContinue}
                disabled={isSubmitting || !ssnReady}
                className={borrowerCompleteChrome.continueOutlineButton(isBranded)}
                size="lg"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Continue
              </Button>
            </div>

            <div className={`text-xs pt-4 border-t ${isBranded ? 'text-muted-foreground' : 'text-gray-500'}`}>
              <p>Request ID: {requestId}</p>
              <p>Completed: {new Date().toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </BorrowerBrandingFrame>
  )
}

